import { useState } from 'react';

import ContactForm from '@/components/public/contact/ContactForm';
import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import Container from '@/components/public/ui/Container';
import Icon from '@/components/public/ui/Icon';
import LocationMap from '@/components/public/ui/LocationMap';
import ViewToggle from '@/components/public/ui/ViewToggle';
import { useSite } from '@/hooks/useSite';

const infoRow = 'flex items-start gap-4';
const iconTile =
  'bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg';

export default function Contact() {
  const site = useSite();
  const locations = site.locations;
  const [activeKey, setActiveKey] = useState(locations[0]?.key ?? '');
  const loc = locations.find((l) => l.key === activeKey) ?? locations[0];

  return (
    <>
      <Seo
        title="Contactos"
        description="Horários de atendimento, morada e formulário de contacto."
      />
      <PageHero
        badge="Fale connosco"
        title="Contactos"
        subtitle="Estamos ao seu dispor para esclarecer dúvidas e prestar apoio."
      />

      <Container className="py-8 pb-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Left column — location switcher scopes the info / hours / map below it */}
          <div className="space-y-6 lg:col-span-5">
            {loc && (
              <>
                {locations.length > 1 && (
                  <div className="border-line border-b">
                    <ViewToggle
                      options={locations.map((l) => ({ label: l.name ?? l.key, value: l.key }))}
                      active={activeKey}
                      onChange={setActiveKey}
                      variant="underline"
                      className="-mb-px"
                    />
                  </div>
                )}

                <div className="surface-card p-6">
                  <h2 className="font-headline text-on-surface mb-6 text-xl font-semibold">
                    Informação de Contacto
                  </h2>
                  <div className="space-y-5">
                    {loc.address && (
                      <div className={infoRow}>
                        <div className={iconTile}>
                          <Icon name="location_on" size="sm" />
                        </div>
                        <div>
                          <p className="text-on-surface font-medium">{loc.name}</p>
                          <p className="text-on-surface-light text-sm">{loc.address}</p>
                        </div>
                      </div>
                    )}

                    {loc.phone && (
                      <div className={infoRow}>
                        <div className={iconTile}>
                          <Icon name="phone" size="sm" />
                        </div>
                        <div>
                          <p className="text-on-surface font-medium">Telefone</p>
                          <p className="text-on-surface-light text-sm">{loc.phone}</p>
                        </div>
                      </div>
                    )}

                    {(loc.email ?? site.contactEmail) && (
                      <div className={infoRow}>
                        <div className={iconTile}>
                          <Icon name="mail" size="sm" />
                        </div>
                        <div>
                          <p className="text-on-surface font-medium">Correio eletrónico</p>
                          <p className="text-on-surface-light text-sm">
                            {loc.email ?? site.contactEmail}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {loc.openingHours && (
                  <div className="surface-card p-6">
                    <h3 className="font-headline text-on-surface mb-4 text-lg font-semibold">
                      Horário de Atendimento
                    </h3>
                    <p className="text-on-surface-light text-sm whitespace-pre-line">
                      {loc.openingHours}
                    </p>
                  </div>
                )}

                {loc.latitude != null && loc.longitude != null && (
                  <div className="border-line h-64 overflow-hidden rounded-lg border">
                    <LocationMap lat={loc.latitude} lng={loc.longitude} />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right column — contact form */}
          <div className="lg:col-span-7">
            <div className="surface-card flex h-full flex-col p-8">
              <h2 className="font-headline text-on-surface mb-2 text-2xl font-semibold">
                Mensagem Geral
              </h2>
              <p className="text-on-surface-light mb-8">
                Preencha o formulário e a nossa equipa administrativa entrará em contacto
                brevemente.
              </p>
              <ContactForm />
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}
